import {
  ForgeButton,
  ForgeDialog,
  ForgeExpansionPanel,
  ForgeIcon,
  ForgeIconButton,
  ForgeList,
  ForgeListItem,
  ForgeOpenIcon,
  ForgeScaffold,
  ForgeTextField,
  ForgeToolbar,
  ForgeTooltip,
} from '@tylertech/forge-react';
import React, { useCallback, useMemo, useRef } from 'react';
import './index.scss';
import { useEscapeKey } from 'common/visualizations/views/agGridReact/customHooks/useEscapeKey';
import { IDialogBeforeCloseEventData } from '@tylertech/forge';
import EventBus from 'common/visualizations/views/agGridReact/helpers/EventBus';
import { I18n } from 'common/i18n';
import { Vif } from 'common/visualizations/vif';
import { findHierarchyInfo } from 'common/visualizations/helpers/AgTableVifHelpers';
import { getGroupingColumnNames } from '../../panes/DataPane/TableHierarchyGroupings/components/TableHierarchyGroupingHeader';
import get from 'lodash/get';
import filter from 'lodash/filter';
import isEmpty from 'lodash/isEmpty';
import SubtotalsSection from './SubtotalsSection';

interface AdvancedTotalingModalProps {
  isTableAdvancedTotalingModalOpen: boolean;
  advancedTotalingModalTitle: string;
  advancedTotalingModalHierarchyId: string;
  vif: Vif;
}

const scope = 'shared.components.advanced_totaling.modal';

const AdvancedTotalingModal: React.FC<AdvancedTotalingModalProps> = ({
  isTableAdvancedTotalingModalOpen,
  advancedTotalingModalTitle,
  advancedTotalingModalHierarchyId,
  vif,
}: AdvancedTotalingModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);

  const hideModal = useCallback(() => {
    EventBus.dispatch('advancedTotaling', {
      advancedTotalingModalTitle: '',
      isTableAdvancedTotalingModalOpen: false,
      advancedTotalingModalHierarchyId: undefined,
    });
  }, []);

  useEscapeKey({
    onKeyup: hideModal,
    enabled: isTableAdvancedTotalingModalOpen,
    eventTarget: modalRef.current || document,
    restoreFocusTarget: '.authoring-modal',
  });

  const selectedColumnGroupings = useMemo(() => {
    const { hierarchies, hierarchyIndex } = findHierarchyInfo(vif, advancedTotalingModalHierarchyId);
    const columnConfigs = filter(
      hierarchies[hierarchyIndex]?.columnConfigurations,
      (config) => config.isGrouping,
    );
    return columnConfigs;
  }, [advancedTotalingModalHierarchyId, vif]);

  const columns = useMemo(() => get(vif, 'series[0].dataSource.dimension.columns', []), [vif]);
  const columnFormats = useMemo(() => get(vif, 'series[0].formatting.columnFormat', {}), [vif]);

  const modalTitle = useMemo(() => {
    return `${I18n.t('title', { scope })} - ${advancedTotalingModalTitle}`;
  }, [advancedTotalingModalTitle]);

  const groupingColumnNames = useMemo(() => {
    return getGroupingColumnNames(selectedColumnGroupings, columns, columnFormats);
  }, [columns, selectedColumnGroupings, columnFormats]);

  const renderBody = useMemo(() => {
    if (isEmpty(groupingColumnNames)) {
      return <></>;
    }

    const totalGroup = groupingColumnNames[0];
    return (
      <>
        <div>
          <span className="forge-typography--body2 group-category">{I18n.t('totals', { scope })}</span>
        </div>
        <ForgeList>
          <ForgeExpansionPanel>
            <ForgeListItem slot={'header'} className={'expansion-header'} key={0}>
              <span className="forge-typography--body1" data-testid="totals-row">
                {totalGroup}
              </span>
              <ForgeOpenIcon slot={'trailing'}></ForgeOpenIcon>
            </ForgeListItem>
            <div>
              <ForgeTextField>
                <label className="forge-typography--label2">{I18n.t('enter_formula', { scope })}</label>
                <textarea data-testid="totals-formula"></textarea>
              </ForgeTextField>
            </div>
          </ForgeExpansionPanel>
        </ForgeList>
        <SubtotalsSection columnGroups={selectedColumnGroupings} columnNames={groupingColumnNames} />
      </>
    );
  }, [selectedColumnGroupings, groupingColumnNames]);

  return (
    <ForgeDialog
      on-forge-dialog-before-close={(e: CustomEvent<IDialogBeforeCloseEventData>) => {
        if (e.detail.reason === 'backdrop') {
          e.preventDefault();
        }
      }}
      on-forge-dialog-close={hideModal}
      open={isTableAdvancedTotalingModalOpen}
      persistent={true}
      ref={modalRef}
      className="advanced-totaling-dialog"
      data-testid="advanced-totaling-dialog"
    >
      <div className={'advanced-totaling-modal'} data-testid="advanced-totaling-modal">
        <ForgeScaffold>
          <ForgeToolbar slot={'header'}>
            <div slot="start" className="modal-title-container">
              <ForgeTooltip placement={'top'} data-testid="advanced-totaling-modal-title-tooltip">
                {modalTitle}
              </ForgeTooltip>
              <h1 className="forge-typography--heading4" data-testid="advanced-totaling-modal-title">
                {modalTitle}
              </h1>
            </div>
            <ForgeIconButton slot={'end'} onClick={hideModal} data-testid="close-btn">
              <ForgeIcon name={'close'}></ForgeIcon>
            </ForgeIconButton>
          </ForgeToolbar>
          <div
            className="advanced-totaling-modal-body"
            data-testid="advanced-totaling-modal-body"
            slot="body"
          >
            {renderBody}
          </div>
          <ForgeToolbar slot={'footer'}>
            <ForgeButton slot={'start'} disabled={true} data-testid="clear-all-formulas-btn">
              <ForgeIcon name={'delete'}></ForgeIcon>
              {I18n.t('clear_all_formulas', { scope })}
            </ForgeButton>
            <ForgeButton slot={'end'} variant={'raised'} data-testid="apply-btn">
              {I18n.t('apply', { scope })}
            </ForgeButton>
          </ForgeToolbar>
        </ForgeScaffold>
      </div>
    </ForgeDialog>
  );
};

export default AdvancedTotalingModal;
